import {MarketIndicatorIcon} from '#/components/indicators/market-indicator-icon';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {SyntheticMarketDataRange} from '#/types/synthetic-market-data-range';
import {decimalsMultiplier} from '#/utils/contract-consts';
import {getNumberPositivityStatus} from '#/utils/number-utils';
import {ClassName, DefaultChildren} from '#/utils/utils';
import {Land, LandMarketInfo} from '@shareland/shareland-common';
import {ComponentProps, FunctionComponent} from 'react';
import {useTranslation} from 'react-i18next';

export const TextColorMarketIndicator: FunctionComponent<
  {
    numberPositivity: ReturnType<typeof getNumberPositivityStatus>;
    className?: '';
  } & DefaultChildren
> = ({numberPositivity, children, className = ''}) => {
  return (
    <span
      className={`data-[number-positivity="positive"]:text-upgreen data-[number-positivity="negative"]:text-downred ${className}`}
      data-number-positivity={numberPositivity}
    >
      {children}
    </span>
  );
};

const getUsdcPriceDirection = (usdcAmount: number) => {
  const priceDifferenceNormalized = usdcAmount / decimalsMultiplier;
  const priceDirection =
    priceDifferenceNormalized >= 0.005 ? 'up' : priceDifferenceNormalized <= -0.005 ? 'down' : 'stable';

  return priceDirection;
};

type LandMarketIndicatorProps = {
  land: Land;
  range: SyntheticMarketDataRange;
  landMarketInfo?: LandMarketInfo;
  priceClassName?: string;
  showPrice?: boolean;
  showDifference?: boolean;
  showRatio?: boolean;
  iconSize: ComponentProps<typeof MarketIndicatorIcon>['size'];
} & ClassName;
export const LandMarketIndicator: FunctionComponent<LandMarketIndicatorProps> = ({
  land,
  range,
  landMarketInfo,
  className = '',
  priceClassName = '',
  showPrice = true,
  showDifference = true,
  showRatio = true,
  iconSize,
}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  const priceDifference = land.syntheticMarketData.marketPrice[range].difference;
  const priceDirection = getUsdcPriceDirection(priceDifference);
  const priceDifferenceFinal = priceDirection === 'stable' ? 0 : priceDifference;
  const priceRatio = land.syntheticMarketData.marketPrice[range].ratio;

  const marketPrice = landMarketInfo?.marketPrice ?? land.marketInfo?.marketPrice;

  return (
    <div className={`flex items-center gap-1.5 overflow-hidden ${className}`}>
      {showPrice && (
        <div className={`flex items-center whitespace-nowrap ${priceClassName}`}>
          {marketPrice
            ? t('utils.stableTokenAmountSqft', {
                currencySymbol: displayPref.symbol,
                amount: displayPref.displayAmount(marketPrice),
              })
            : t('symbols.doubleDash')}
        </div>
      )}
      <MarketIndicatorIcon size={iconSize} direction={priceDirection} />
      {showDifference && (
        <span>
          {t('utils.stableAmount', {
            amount: displayPref.displayAmount(priceDifferenceFinal),
          })}
        </span>
      )}
      {showRatio && (
        <span>
          {t('utils.percentageParentheses', {
            value: priceRatio,
          })}
        </span>
      )}
    </div>
  );
};

type DifferenceRatioMarketIndicatorProps = {
  priceDifference: number;
  marketIndicatorIconSize: ComponentProps<typeof MarketIndicatorIcon>['size'];
} & ClassName;
export const DifferenceMarketIndicator: FunctionComponent<DifferenceRatioMarketIndicatorProps> = ({
  priceDifference,
  marketIndicatorIconSize,
}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  const priceDirection = getUsdcPriceDirection(priceDifference);
  const priceDifferenceFinal = priceDirection === 'stable' ? 0 : priceDifference;

  return (
    <>
      <MarketIndicatorIcon direction={priceDirection} size={marketIndicatorIconSize} />
      <span>
        {t('utils.stableTokenAmount', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(priceDifferenceFinal),
        })}
      </span>
    </>
  );
};
